import { useEffect, useRef } from 'react';

// SPEC-021: Video embed player. v1 uses a plain iframe so we don't pull
// in the YouTube IFrame API or Vimeo Player.js. Progress tracking is
// best-effort — we fire an `open` event when the iframe first becomes
// visible and rely on the outer heartbeat loop for the rest. Follow-up
// phase can wire the provider SDKs for precise timeupdate events.

interface VideoEmbedPlayerProps {
  provider: 'youtube' | 'vimeo';
  videoId: string;
  url?: string;
  onOpen?: () => void;
}

export function VideoEmbedPlayer({ provider, videoId, url, onOpen }: VideoEmbedPlayerProps) {
  const openedRef = useRef(false);

  useEffect(() => {
    if (openedRef.current) return;
    openedRef.current = true;
    onOpen?.();
  }, [videoId, onOpen]);

  const src =
    provider === 'youtube'
      ? `https://www.youtube.com/embed/${videoId}?playsinline=1&rel=0`
      : `https://player.vimeo.com/video/${videoId}?playsinline=1`;

  return (
    <div className="overflow-hidden rounded-xl bg-black">
      <div className="relative aspect-video w-full">
        <iframe
          src={src}
          title={`${provider} video ${videoId}`}
          className="absolute inset-0 h-full w-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
      {url && (
        <div className="px-3 py-2 text-[10px] text-[var(--color-muted)]">
          {url}
        </div>
      )}
    </div>
  );
}
